import React, {useCallback, useEffect, useState} from "react";
import {connect, useDispatch, useSelector} from "react-redux";
import {RouteComponentProps} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {CenterBox, WideContent} from "@pg-design/grid-module";
import {borderRadius, calculateRemSize, flexAbsoluteCenter, mb, p, pb, position, positionRelative, zIndex} from "@pg-design/helpers-css";
import {HorizontalListIcon, InfoIcon, SettingsIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {usePrevious} from "@pg-mono/hooks";
import {isEmpty, omit} from "@pg-mono/nodash";
import {appendQueryString, IQuery, parseSearch} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";
import {IOfferFriendlyAbroadRegionUrlParams, rpAppLink, rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";
import {navigationHeight} from "@pg-rp/navigation";

import {IRPStore} from "../../app/rp_reducer";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {isRailTransportAvailable} from "../../maps/utils/is_rail_transport_available";
import {Country} from "../../region/types/Country";
import {SlotOrderType} from "../../search/components/search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchFormWithDrawer} from "../../search/components/SearchFormWithDrawer";
import {searchFormWithDrawerHeight} from "../../search/constants/search_form_with_drawer_height";
import {useSearchForm} from "../../search/hooks/use_search_form";
import {ISearchFiltersFormValues} from "../../search/types/ISearchFiltersFormValues";
import {BoundAction} from "../../store/utils/bound_action";
import {openSidebarHit, sidebarCloseHit} from "../../tracking/algolytics_hits/map_interaction_hits";
import {ISearchRegion, offerListSearchHit, prepareOfferListSearchData} from "../../tracking/algolytics_hits/offer_list_search_hit";
import {gtmEventOffersCount} from "../../tracking/google_tag_manager/gtm_event_ui";
import {ILatestQuery, parseQueryParamToInt} from "../../utils/latest_query";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {ViewType} from "../../view_type/ViewType";
import {MapOfferPreview} from "../components/map_offer/MapOfferPreview";
import {OfferListMap} from "../components/OfferListMap";
import {ExpandablePanelMapHeader} from "../components/os_map_for_offers/ExpandablePanelMapHeader";
import {ExpandablePanelMapLayers} from "../components/os_map_for_offers/ExpandablePanelMapLayers";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../detail/components/property_list/OfferDetailPropertyListTable";
import {useOfferListMapTransportRoutes} from "../hooks/use_offer_list_map_transport_routes";
import {ISelectedRegion} from "../list/actions/offer_list_selected_data_actions";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../list/components/OfferListPropertiesModal";
import {getCountryId} from "../list/helpers/sub_type/get_fetch_offer_list_query";
import {fetchMapSelectedOffer, resetFetchMapSelectedOffer} from "../map/actions/fetch_map_offer_detail";
import {useGetMapRegionsQuery} from "../map/api/get_map_region";
import {MapGeneratedDescription} from "../map/components/MapGeneratedDescription";
import {MapHeader} from "../map/components/MapHeader";
import {IMapOfferSidebar} from "../map/components/MapOfferSidebar";
import {IOfferListMapListStore} from "../map/reducers/map_offer_list_reducer";
import {gtmEventMapList, MapGTMActions, MapGTMLabel} from "../map/utils/map_analytics";
import {OfferType} from "../types/OfferType";

interface IStateProps {
    offerListData: IOfferListMapListStore;
    pageTitle: string;
    selectedRegion: ISelectedRegion[];
    regionTypes: IValueLabel[];
    offer: IMapOfferSidebar;
    offerRequestState: RequestState;
    appliedOffers: number[];
    favouriteOffers: number[];
    latestQuery: ILatestQuery;
}

interface IActionProps {
    fetchMapSelectedOffer: BoundAction<typeof fetchMapSelectedOffer>;
    resetFetchMapSelectedOffer: BoundAction<typeof resetFetchMapSelectedOffer>;
}

interface IOwnProps extends RouteComponentProps<{}> {}

interface IProps extends IStateProps, IActionProps, IOwnProps {}

const OfferListMapViewC = (props: IProps) => {
    /**
     * State
     */
    const {isMobile} = useUserDevice();
    const [isHoliday, setIsHoliday] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showGeneratedDescription, setShowGeneratedDescription] = useState(false);
    const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
    const [isPricePanelOpen, setIsPricePanelOpen] = useState(false);
    const theme = useTheme();
    const {transportRoutes} = useOfferListMapTransportRoutes();
    const hasRailTransport = isRailTransportAvailable(transportRoutes);
    const travelTime = useSelector((state: IRPStore) => state.search.travelTime);
    const {data: offerListRegion} = useGetMapRegionsQuery({region: props.selectedRegion.map((region) => region.id)});

    const generatedDescriptionAllowed =
        offerListRegion?.results.length === 1 &&
        !!props.latestQuery.type &&
        parseQueryParamToInt(props.latestQuery.type) === OfferType.FLAT &&
        isEmpty(omit(props.latestQuery, ["type", "region"]));

    /**
     * Lifecycle
     */
    useEffect(() => {
        const query = parseSearch(props.location.search);
        const isHoliday = query.is_holiday === "1";
        setIsInitialized(true);
        setIsHoliday(isHoliday);
        if (props.offerListData.requestState === RequestState.Success) {
            hitSearch();
        }
    }, []);

    const prevOfferListDataRequestState = usePrevious(props.offerListData.requestState, RequestState.None);
    useEffect(() => {
        if (prevOfferListDataRequestState === RequestState.Waiting && props.offerListData.requestState === RequestState.Success) {
            hitSearch();
        }
    }, [props.offerListData.requestState]);

    // Top Offset of the blend under search fields
    const blendHeightForFiltersWithDrawer = `${navigationHeight + searchFormWithDrawerHeight}px`;

    /**
     * Sidebar implementation
     */
    const dispatch = useDispatch();
    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), props.offerListData.latestQuery);

    /**
     * Callbacks
     */

    const {openLocationSearch} = useSearchForm();
    const handleMapHeaderClick = () => {
        openLocationSearch();
    };

    // map will flicker without useCallback
    const onMarkerClick = useCallback((offerId: number) => {
        setIsSidebarOpen(true);
        gtmEventMapList(MapGTMActions.PIN, offerId);
        openSidebarHit(ViewType.OFFER_LIST_MAP, offerId);
        props.fetchMapSelectedOffer(offerId);
    }, []);

    // Sidebar implementation
    const onOfferSidebarClose = () => {
        setIsSidebarOpen(false);
        gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.CLOSE);
        sidebarCloseHit(ViewType.OFFER_LIST_MAP);
        props.resetFetchMapSelectedOffer();
    };

    const getGoBackToListUrl = () => {
        const {friendlySlug, country} = props.match.params as {friendlySlug: string | undefined; country?: string; abroadRegionSlug?: string};
        if (getCountryId(country) !== Country.POLAND) {
            const url = rpAppLink.offer.listFriendlyAbroad(props.match.params as IOfferFriendlyAbroadRegionUrlParams);
            return appendQueryString(url, omit(props.offerListData.latestQuery, ["country", "region", "region_name", "type"]));
        }

        if (friendlySlug) {
            return rpAppLink.offer.listFriendly({friendlySlug});
        }

        if (props.location.pathname === rpAppPath.multiOfferList.map) {
            const url = rpAppLink.offer.multiList();
            return appendQueryString(url, props.offerListData.latestQuery as IQuery);
        }
        return rpAppLink.offer.list();
    };

    const hitSearch = () => {
        const {count, propertiesCount, latestQuery, offers} = props.offerListData;
        const {selectedRegion: selectedRegions} = props;
        const selectedSearchRegions: ISearchRegion[] = selectedRegions.reduce((acc: ISearchRegion[], region: ISelectedRegion) => {
            const regionType = region && props.regionTypes?.find((t) => t.value === region.type);
            return [...acc, {...region, type__display: regionType ? regionType.label.toLocaleLowerCase() : ""}];
        }, []);

        const searchData = prepareOfferListSearchData(
            count,
            propertiesCount,
            latestQuery,
            selectedSearchRegions,
            parseSearch(props.location.search), // needed in case of `s` query-param is present
            null, // we have no info about previous view
            travelTime
        );

        const offerIds: number[] = [];
        const offerNames: string[] = [];
        const vendorIds: number[] = [];
        const vendorNames: string[] = [];
        offers.forEach((offer) => {
            offerIds.push(offer && offer.id);
            offerNames.push(offer && offer.name);
            vendorIds.push(offer.vendor && offer.vendor.id);
            vendorNames.push(offer.vendor && offer.vendor.name);
        });

        // When sidebar is open and filters are changing update properties list
        if (isSidebarOpen && props.offer) {
            fetchOfferDetailProperties(
                props.offer.id,
                PropertyListTabTypes.MODAL,
                DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE,
                {page: 1},
                {...toQueryValues(propertyListTableFiltersFields, latestSearchFilters)}
            )(dispatch);
        }

        // gtm
        gtmEventOffersCount(count);
        // algolytics
        offerListSearchHit(ViewType.OFFER_LIST_MAP, searchData, offerIds, offerNames, vendorIds, vendorNames, []);
        // FacebookPixel.track("Search");
    };

    const handleListButtonClick = () => gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.LISTING);

    const generatedDescription = offerListRegion?.results.length ? (
        <MapGeneratedDescription
            generatedDescriptionAllowed={generatedDescriptionAllowed}
            onHideGenericDescription={() => setShowGeneratedDescription(false)}
            region={offerListRegion?.results[0]}
            showGeneratedDescription={showGeneratedDescription}
        />
    ) : null;

    /**
     * Render
     */

    if (!isInitialized) {
        return (
            <div css={viewLoaderHolder}>
                <CenterBox>
                    <Loader size="lg" />
                </CenterBox>

                {generatedDescription}
            </div>
        );
    }

    return (
        <div css={[positionRelative]}>
            <div css={offerListMapWrapper}>
                <div css={searchWrapper}>
                    <ExpandablePanelMapHeader
                        isOpen={isInfoPanelOpen}
                        generatedDescriptionAllowed={generatedDescriptionAllowed}
                        handleClose={() => setIsInfoPanelOpen(false)}
                        onShowGeneratedDescription={() => setShowGeneratedDescription(true)}
                    />
                    <ExpandablePanelMapLayers transformRoutes={transportRoutes} isOpen={isPricePanelOpen} handleClose={() => setIsPricePanelOpen(false)} />
                    <WideContent>
                        <SearchFormWithDrawer
                            listFriendlyLink={rpAppLink.offer.mapFriendly}
                            abroadListFriendlyLink={({
                                friendlySlug,
                                country,
                                abroadRegionSlug
                            }: {
                                friendlySlug: string;
                                country: string;
                                abroadRegionSlug?: string;
                            }) => {
                                return abroadRegionSlug
                                    ? rpAppLink.offer.mapAbroadRegion({
                                          friendlySlug,
                                          country,
                                          abroadRegionSlug
                                      })
                                    : rpAppLink.offer.mapAbroadCountry({friendlySlug, country});
                            }}
                            multiRegionLink={rpAppLink.offer.multiListMap}
                            latestQuery={props.offerListData.latestQuery}
                            blendStyles={searchAndFiltersStyles}
                            sortActive={false}
                            // Blend will start on the bottom line of the Search component
                            // TODO: Sometimes blend is relative to their elements, navigationHeight should be removed from atoms/DesktopBlend implementation
                            blendTop={`${searchFormWithDrawerHeight - navigationHeight}px`}
                            blendHeightDecrease={blendHeightForFiltersWithDrawer}
                            isMap
                            slotOrder={SlotOrderType.ROW_REVERSE}
                            showTopFilters
                            css={[mb(0), zIndex(2)]}
                        />
                    </WideContent>
                </div>

                <div css={mapHolder}>
                    {hasRailTransport && (
                        <button
                            css={[sideButton, {top: calculateRemSize(3)}]}
                            onClick={() => {
                                setIsPricePanelOpen((prev) => !prev);
                                setIsInfoPanelOpen(false);
                            }}
                        >
                            <SettingsIcon
                                size="2.4"
                                wrapperColor="transparent"
                                wrapperSize="2.4"
                                fill={isPricePanelOpen ? theme.colors.gray[600] : theme.colors.secondary}
                            />
                        </button>
                    )}
                    <button
                        css={[sideButton, {top: calculateRemSize(11)}]}
                        onClick={() => {
                            setIsInfoPanelOpen((prev) => !prev);
                            setIsPricePanelOpen(false);
                        }}
                    >
                        <InfoIcon
                            size="2.4"
                            wrapperColor="transparent"
                            wrapperSize="2.4"
                            fill={isInfoPanelOpen ? theme.colors.gray[600] : theme.colors.secondary}
                        />
                    </button>
                    <MapSize fullWidth={!isSidebarOpen}>
                        <OfferListMap
                            transportRoutes={transportRoutes}
                            onMarkerClick={onMarkerClick}
                            offers={props.offerListData.offers}
                            offersRequestState={props.offerListData.requestState}
                            isHoliday={isHoliday}
                        />
                    </MapSize>

                    <>
                        <Button
                            href={getGoBackToListUrl()}
                            onClick={handleListButtonClick}
                            iconLeft={HorizontalListIcon}
                            css={listButton}
                            variant="filled_primary"
                        >
                            Lista
                        </Button>
                        {!isMobile && (
                            <MapHeader
                                pageTitle={props.pageTitle}
                                offerCount={props.offerListData.count}
                                propertiesCount={props.offerListData.propertiesCount}
                                selectedRegion={props.selectedRegion}
                                generatedDescriptionAllowed={generatedDescriptionAllowed}
                                onShowGeneratedDescription={() => setShowGeneratedDescription(true)}
                                onHeaderClick={handleMapHeaderClick}
                            />
                        )}
                    </>
                    {props.offer && (
                        <MapOfferPreview
                            offer={props.offer}
                            onClose={onOfferSidebarClose}
                            requestState={props.offerRequestState}
                            latestQuery={props.offerListData.latestQuery}
                            isOpen={isSidebarOpen}
                        />
                    )}

                    {generatedDescription}
                </div>
            </div>
            <LeadModal />
        </div>
    );
};

export const OfferListMapView = connect(mapStateToProps, mapActionsToProps)(OfferListMapViewC);

/**
 * Connect
 */

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        offer: state.offerListMap.selectedOffer.data,
        offerRequestState: state.offerListMap.selectedOffer.requestState,
        offerListData: state.offerListMap.list,
        selectedRegion: state.offerListMap.selectedRegion,
        regionTypes: state.variables.data ? state.variables.data.region_type : [],
        pageTitle: state.metaData.standardMetaData.title,
        appliedOffers: state.offerListMap.appliedOffers.data,
        favouriteOffers: state.favourite.favourites.offers,
        latestQuery: state.offerListMap.list.latestQuery
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionProps {
    return bindActionCreators(
        {
            fetchMapSelectedOffer: fetchMapSelectedOffer,
            resetFetchMapSelectedOffer: resetFetchMapSelectedOffer
        },
        dispatch
    );
}

const viewLoaderHolder = css`
    height: calc(100vh - 13rem);
`;

export const mapHolder = css`
    position: relative;
    display: flex;
    flex-direction: row;
    flex: 1;
    overflow-x: hidden;

    .gm-style-iw-c {
        border-radius: ${borderRadius(0.5)};
        padding-right: 15px !important;
        box-shadow: 0 2px 20px rgba(0, 0, 0, 0.2);

        button {
            top: 3px;
            right: 3px;
            display: none !important;
        }
    }
`;

export const MapSize = styled.div<{fullWidth: boolean}>`
    position: relative;
    display: flex;
    width: 100%;
    transition: margin-right 500ms;
    transition-delay: 50ms;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        margin-right: ${(props) => (props.fullWidth ? "0" : "42%")};
    }
`;

export const listButton = (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        min-width: 15.6rem;
        padding-left: 4rem;
        padding-right: 4rem;
        display: block;
        position: absolute;
        top: 2.5rem;
        left: 2.5rem;
    }
`;

const offerListMapWrapper = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 61px);
    width: 100%;

    @media (min-width: ${theme.breakpoints.md}) {
        height: calc(100vh - 60px);
    }
`;

const searchAndFiltersStyles = css`
    @media (min-width: 1024px) {
        top: 15.9rem;
        height: calc(100vh - 30.2rem);
    }

    @media (min-width: 1200px) {
        top: 9rem;
        height: calc(100vh - 233px);
    }
`;

const searchWrapper = (theme: Theme) => css`
    display: block;
    width: 100%;
    height: auto;
    background-color: #fff;
    ${pb(3)};
    z-index: 2;
    ${elevation(4)};

    @media (min-width: ${theme.breakpoints.md}) {
        background-color: initial;
        ${pb(0)};
        ${elevation(0)};
    }
`;

const sideButton = (theme: Theme) => css`
    ${flexAbsoluteCenter};
    ${borderRadius(0.5, 0, 0, 0.5)};
    ${position("absolute")};
    ${p(1)};
    right: 0;
    background-color: #fff;
    z-index: 1;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;
import React, {useCallback, useEffect, useState} from "react";
import {connect, useDispatch, useSelector} from "react-redux";
import {RouteComponentProps} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {CenterBox, WideContent} from "@pg-design/grid-module";
import {borderRadius, calculateRemSize, flexAbsoluteCenter, mb, p, pb, position, positionRelative, zIndex} from "@pg-design/helpers-css";
import {HorizontalListIcon, InfoIcon, SettingsIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {usePrevious} from "@pg-mono/hooks";
import {isEmpty, omit} from "@pg-mono/nodash";
import {appendQueryString, IQuery, parseSearch} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";
import {IOfferFriendlyAbroadRegionUrlParams, rpAppLink, rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";
import {navigationHeight} from "@pg-rp/navigation";

import {IRPStore} from "../../app/rp_reducer";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {isRailTransportAvailable} from "../../maps/utils/is_rail_transport_available";
import {Country} from "../../region/types/Country";
import {SlotOrderType} from "../../search/components/search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchFormWithDrawer} from "../../search/components/SearchFormWithDrawer";
import {searchFormWithDrawerHeight} from "../../search/constants/search_form_with_drawer_height";
import {useSearchForm} from "../../search/hooks/use_search_form";
import {ISearchFiltersFormValues} from "../../search/types/ISearchFiltersFormValues";
import {BoundAction} from "../../store/utils/bound_action";
import {openSidebarHit, sidebarCloseHit} from "../../tracking/algolytics_hits/map_interaction_hits";
import {ISearchRegion, offerListSearchHit, prepareOfferListSearchData} from "../../tracking/algolytics_hits/offer_list_search_hit";
import {gtmEventOffersCount} from "../../tracking/google_tag_manager/gtm_event_ui";
import {ILatestQuery, parseQueryParamToInt} from "../../utils/latest_query";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {ViewType} from "../../view_type/ViewType";
import {MapOfferPreview} from "../components/map_offer/MapOfferPreview";
import {OfferListMap} from "../components/OfferListMap";
import {ExpandablePanelMapHeader} from "../components/os_map_for_offers/ExpandablePanelMapHeader";
import {ExpandablePanelMapLayers} from "../components/os_map_for_offers/ExpandablePanelMapLayers";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../detail/components/property_list/OfferDetailPropertyListTable";
import {useOfferListMapTransportRoutes} from "../hooks/use_offer_list_map_transport_routes";
import {ISelectedRegion} from "../list/actions/offer_list_selected_data_actions";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../list/components/OfferListPropertiesModal";
import {getCountryId} from "../list/helpers/sub_type/get_fetch_offer_list_query";
import {fetchMapSelectedOffer, resetFetchMapSelectedOffer} from "../map/actions/fetch_map_offer_detail";
import {useGetMapRegionsQuery} from "../map/api/get_map_region";
import {MapGeneratedDescription} from "../map/components/MapGeneratedDescription";
import {MapHeader} from "../map/components/MapHeader";
import {IMapOfferSidebar} from "../map/components/MapOfferSidebar";
import {IOfferListMapListStore} from "../map/reducers/map_offer_list_reducer";
import {gtmEventMapList, MapGTMActions, MapGTMLabel} from "../map/utils/map_analytics";
import {OfferType} from "../types/OfferType";

interface IStateProps {
    offerListData: IOfferListMapListStore;
    pageTitle: string;
    selectedRegion: ISelectedRegion[];
    regionTypes: IValueLabel[];
    offer: IMapOfferSidebar;
    offerRequestState: RequestState;
    appliedOffers: number[];
    favouriteOffers: number[];
    latestQuery: ILatestQuery;
}

interface IActionProps {
    fetchMapSelectedOffer: BoundAction<typeof fetchMapSelectedOffer>;
    resetFetchMapSelectedOffer: BoundAction<typeof resetFetchMapSelectedOffer>;
}

interface IOwnProps extends RouteComponentProps<{}> {}

interface IProps extends IStateProps, IActionProps, IOwnProps {}

const OfferListMapViewC = (props: IProps) => {
    /**
     * State
     */
    const {isMobile} = useUserDevice();
    const [isHoliday, setIsHoliday] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showGeneratedDescription, setShowGeneratedDescription] = useState(false);
    const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
    const [isPricePanelOpen, setIsPricePanelOpen] = useState(false);
    const theme = useTheme();
    const {transportRoutes} = useOfferListMapTransportRoutes();
    const hasRailTransport = isRailTransportAvailable(transportRoutes);
    const travelTime = useSelector((state: IRPStore) => state.search.travelTime);
    const {data: offerListRegion} = useGetMapRegionsQuery({region: props.selectedRegion.map((region) => region.id)});

    const generatedDescriptionAllowed =
        offerListRegion?.results.length === 1 &&
        !!props.latestQuery.type &&
        parseQueryParamToInt(props.latestQuery.type) === OfferType.FLAT &&
        isEmpty(omit(props.latestQuery, ["type", "region"]));

    /**
     * Lifecycle
     */
    useEffect(() => {
        const query = parseSearch(props.location.search);
        const isHoliday = query.is_holiday === "1";
        setIsInitialized(true);
        setIsHoliday(isHoliday);
        if (props.offerListData.requestState === RequestState.Success) {
            hitSearch();
        }
    }, []);

    const prevOfferListDataRequestState = usePrevious(props.offerListData.requestState, RequestState.None);
    useEffect(() => {
        if (prevOfferListDataRequestState === RequestState.Waiting && props.offerListData.requestState === RequestState.Success) {
            hitSearch();
        }
    }, [props.offerListData.requestState]);

    // Top Offset of the blend under search fields
    const blendHeightForFiltersWithDrawer = `${navigationHeight + searchFormWithDrawerHeight}px`;

    /**
     * Sidebar implementation
     */
    const dispatch = useDispatch();
    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), props.offerListData.latestQuery);

    /**
     * Callbacks
     */

    const {openLocationSearch} = useSearchForm();
    const handleMapHeaderClick = () => {
        openLocationSearch();
    };

    // map will flicker without useCallback
    const onMarkerClick = useCallback((offerId: number) => {
        setIsSidebarOpen(true);
        gtmEventMapList(MapGTMActions.PIN, offerId);
        openSidebarHit(ViewType.OFFER_LIST_MAP, offerId);
        props.fetchMapSelectedOffer(offerId);
    }, []);

    // Sidebar implementation
    const onOfferSidebarClose = () => {
        setIsSidebarOpen(false);
        gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.CLOSE);
        sidebarCloseHit(ViewType.OFFER_LIST_MAP);
        props.resetFetchMapSelectedOffer();
    };

    const getGoBackToListUrl = () => {
        const {friendlySlug, country} = props.match.params as {friendlySlug: string | undefined; country?: string; abroadRegionSlug?: string};
        if (getCountryId(country) !== Country.POLAND) {
            const url = rpAppLink.offer.listFriendlyAbroad(props.match.params as IOfferFriendlyAbroadRegionUrlParams);
            return appendQueryString(url, omit(props.offerListData.latestQuery, ["country", "region", "region_name", "type"]));
        }

        if (friendlySlug) {
            return rpAppLink.offer.listFriendly({friendlySlug});
        }

        if (props.location.pathname === rpAppPath.multiOfferList.map) {
            const url = rpAppLink.offer.multiList();
            return appendQueryString(url, props.offerListData.latestQuery as IQuery);
        }
        return rpAppLink.offer.list();
    };

    const hitSearch = () => {
        const {count, propertiesCount, latestQuery, offers} = props.offerListData;
        const {selectedRegion: selectedRegions} = props;
        const selectedSearchRegions: ISearchRegion[] = selectedRegions.reduce((acc: ISearchRegion[], region: ISelectedRegion) => {
            const regionType = region && props.regionTypes?.find((t) => t.value === region.type);
            return [...acc, {...region, type__display: regionType ? regionType.label.toLocaleLowerCase() : ""}];
        }, []);

        const searchData = prepareOfferListSearchData(
            count,
            propertiesCount,
            latestQuery,
            selectedSearchRegions,
            parseSearch(props.location.search), // needed in case of `s` query-param is present
            null, // we have no info about previous view
            travelTime
        );

        const offerIds: number[] = [];
        const offerNames: string[] = [];
        const vendorIds: number[] = [];
        const vendorNames: string[] = [];
        offers.forEach((offer) => {
            offerIds.push(offer && offer.id);
            offerNames.push(offer && offer.name);
            vendorIds.push(offer.vendor && offer.vendor.id);
            vendorNames.push(offer.vendor && offer.vendor.name);
        });

        // When sidebar is open and filters are changing update properties list
        if (isSidebarOpen && props.offer) {
            fetchOfferDetailProperties(
                props.offer.id,
                PropertyListTabTypes.MODAL,
                DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE,
                {page: 1},
                {...toQueryValues(propertyListTableFiltersFields, latestSearchFilters)}
            )(dispatch);
        }

        // gtm
        gtmEventOffersCount(count);
        // algolytics
        offerListSearchHit(ViewType.OFFER_LIST_MAP, searchData, offerIds, offerNames, vendorIds, vendorNames, []);
        // FacebookPixel.track("Search");
    };

    const handleListButtonClick = () => gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.LISTING);

    const generatedDescription = offerListRegion?.results.length ? (
        <MapGeneratedDescription
            generatedDescriptionAllowed={generatedDescriptionAllowed}
            onHideGenericDescription={() => setShowGeneratedDescription(false)}
            region={offerListRegion?.results[0]}
            showGeneratedDescription={showGeneratedDescription}
        />
    ) : null;

    /**
     * Render
     */

    if (!isInitialized) {
        return (
            <div css={viewLoaderHolder}>
                <CenterBox>
                    <Loader size="lg" />
                </CenterBox>

                {generatedDescription}
            </div>
        );
    }

    return (
        <div css={[positionRelative]}>
            <div css={offerListMapWrapper}>
                <div css={searchWrapper}>
                    <ExpandablePanelMapHeader
                        isOpen={isInfoPanelOpen}
                        generatedDescriptionAllowed={generatedDescriptionAllowed}
                        handleClose={() => setIsInfoPanelOpen(false)}
                        onShowGeneratedDescription={() => setShowGeneratedDescription(true)}
                    />
                    <ExpandablePanelMapLayers transformRoutes={transportRoutes} isOpen={isPricePanelOpen} handleClose={() => setIsPricePanelOpen(false)} />
                    <WideContent>
                        <SearchFormWithDrawer
                            listFriendlyLink={rpAppLink.offer.mapFriendly}
                            abroadListFriendlyLink={({
                                friendlySlug,
                                country,
                                abroadRegionSlug
                            }: {
                                friendlySlug: string;
                                country: string;
                                abroadRegionSlug?: string;
                            }) => {
                                return abroadRegionSlug
                                    ? rpAppLink.offer.mapAbroadRegion({
                                          friendlySlug,
                                          country,
                                          abroadRegionSlug
                                      })
                                    : rpAppLink.offer.mapAbroadCountry({friendlySlug, country});
                            }}
                            multiRegionLink={rpAppLink.offer.multiListMap}
                            latestQuery={props.offerListData.latestQuery}
                            blendStyles={searchAndFiltersStyles}
                            sortActive={false}
                            // Blend will start on the bottom line of the Search component
                            // TODO: Sometimes blend is relative to their elements, navigationHeight should be removed from atoms/DesktopBlend implementation
                            blendTop={`${searchFormWithDrawerHeight - navigationHeight}px`}
                            blendHeightDecrease={blendHeightForFiltersWithDrawer}
                            isMap
                            slotOrder={SlotOrderType.ROW_REVERSE}
                            showTopFilters
                            css={[mb(0), zIndex(2)]}
                        />
                    </WideContent>
                </div>

                <div css={mapHolder}>
                    {hasRailTransport && (
                        <button
                            css={[sideButton, {top: calculateRemSize(3)}]}
                            onClick={() => {
                                setIsPricePanelOpen((prev) => !prev);
                                setIsInfoPanelOpen(false);
                            }}
                        >
                            <SettingsIcon
                                size="2.4"
                                wrapperColor="transparent"
                                wrapperSize="2.4"
                                fill={isPricePanelOpen ? theme.colors.gray[600] : theme.colors.secondary}
                            />
                        </button>
                    )}
                    <button
                        css={[sideButton, {top: calculateRemSize(11)}]}
                        onClick={() => {
                            setIsInfoPanelOpen((prev) => !prev);
                            setIsPricePanelOpen(false);
                        }}
                    >
                        <InfoIcon
                            size="2.4"
                            wrapperColor="transparent"
                            wrapperSize="2.4"
                            fill={isInfoPanelOpen ? theme.colors.gray[600] : theme.colors.secondary}
                        />
                    </button>
                    <MapSize fullWidth={!isSidebarOpen}>
                        <OfferListMap
                            transportRoutes={transportRoutes}
                            onMarkerClick={onMarkerClick}
                            offers={props.offerListData.offers}
                            offersRequestState={props.offerListData.requestState}
                            isHoliday={isHoliday}
                        />
                    </MapSize>

                    <>
                        <Button
                            href={getGoBackToListUrl()}
                            onClick={handleListButtonClick}
                            iconLeft={HorizontalListIcon}
                            css={listButton}
                            variant="filled_primary"
                        >
                            Lista
                        </Button>
                        {!isMobile && (
                            <MapHeader
                                pageTitle={props.pageTitle}
                                offerCount={props.offerListData.count}
                                propertiesCount={props.offerListData.propertiesCount}
                                selectedRegion={props.selectedRegion}
                                generatedDescriptionAllowed={generatedDescriptionAllowed}
                                onShowGeneratedDescription={() => setShowGeneratedDescription(true)}
                                onHeaderClick={handleMapHeaderClick}
                            />
                        )}
                    </>
                    {props.offer && (
                        <MapOfferPreview
                            offer={props.offer}
                            onClose={onOfferSidebarClose}
                            requestState={props.offerRequestState}
                            latestQuery={props.offerListData.latestQuery}
                            isOpen={isSidebarOpen}
                        />
                    )}

                    {generatedDescription}
                </div>
            </div>
            <LeadModal />
        </div>
    );
};

export const OfferListMapView = connect(mapStateToProps, mapActionsToProps)(OfferListMapViewC);

/**
 * Connect
 */

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        offer: state.offerListMap.selectedOffer.data,
        offerRequestState: state.offerListMap.selectedOffer.requestState,
        offerListData: state.offerListMap.list,
        selectedRegion: state.offerListMap.selectedRegion,
        regionTypes: state.variables.data ? state.variables.data.region_type : [],
        pageTitle: state.metaData.standardMetaData.title,
        appliedOffers: state.offerListMap.appliedOffers.data,
        favouriteOffers: state.favourite.favourites.offers,
        latestQuery: state.offerListMap.list.latestQuery
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionProps {
    return bindActionCreators(
        {
            fetchMapSelectedOffer: fetchMapSelectedOffer,
            resetFetchMapSelectedOffer: resetFetchMapSelectedOffer
        },
        dispatch
    );
}

const viewLoaderHolder = css`
    height: calc(100vh - 13rem);
`;

export const mapHolder = css`
    position: relative;
    display: flex;
    flex-direction: row;
    flex: 1;
    overflow-x: hidden;

    .gm-style-iw-c {
        border-radius: ${borderRadius(0.5)};
        padding-right: 15px !important;
        box-shadow: 0 2px 20px rgba(0, 0, 0, 0.2);

        button {
            top: 3px;
            right: 3px;
            display: none !important;
        }
    }
`;

export const MapSize = styled.div<{fullWidth: boolean}>`
    position: relative;
    display: flex;
    width: 100%;
    transition: margin-right 500ms;
    transition-delay: 50ms;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        margin-right: ${(props) => (props.fullWidth ? "0" : "42%")};
    }
`;

export const listButton = (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        min-width: 15.6rem;
        padding-left: 4rem;
        padding-right: 4rem;
        display: block;
        position: absolute;
        top: 2.5rem;
        left: 2.5rem;
    }
`;

const offerListMapWrapper = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 61px);
    width: 100%;

    @media (min-width: ${theme.breakpoints.md}) {
        height: calc(100vh - 60px);
    }
`;

const searchAndFiltersStyles = css`
    @media (min-width: 1024px) {
        top: 15.9rem;
        height: calc(100vh - 30.2rem);
    }

    @media (min-width: 1200px) {
        top: 9rem;
        height: calc(100vh - 233px);
    }
`;

const searchWrapper = (theme: Theme) => css`
    display: block;
    width: 100%;
    height: auto;
    background-color: #fff;
    ${pb(3)};
    z-index: 2;
    ${elevation(4)};

    @media (min-width: ${theme.breakpoints.md}) {
        background-color: initial;
        ${pb(0)};
        ${elevation(0)};
    }
`;

const sideButton = (theme: Theme) => css`
    ${flexAbsoluteCenter};
    ${borderRadius(0.5, 0, 0, 0.5)};
    ${position("absolute")};
    ${p(1)};
    right: 0;
    background-color: #fff;
    z-index: 1;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;
